import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from 'tea-component';
import './ChartErrorBoundary.scss';

interface IProps {
  children: ReactNode;
  onReset?: () => void;
}

interface IState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

/**
 * 图表渲染错误边界组件
 * 捕获图表渲染过程中的所有异常
 */
class ChartErrorBoundary extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<IState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ChartErrorBoundary] 捕获错误:', {
      error,
      errorInfo,
      timestamp: new Date().toISOString(),
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="dba-chart-error-boundary">
          <div className="dba-error-container">
            <div className="dba-error-icon">⚠️</div>
            <h3 className="dba-error-title">图表渲染失败</h3>
            <p className="dba-error-message">{this.state.error?.message || '未知错误'}</p>
            <div className="dba-error-actions">
              <Button type="primary" onClick={this.handleReset}>
                重新渲染
              </Button>
              <Button onClick={this.toggleDetails}>
                {this.state.showDetails ? '隐藏详情' : '查看详情'}
              </Button>
            </div>
            {this.state.showDetails && (
              <div className="dba-error-details">
                <h4>错误堆栈:</h4>
                <pre className="dba-error-stack">{this.state.error?.stack}</pre>
                {this.state.errorInfo?.componentStack && (
                  <>
                    <h4>组件堆栈:</h4>
                    <pre className="dba-error-stack">{this.state.errorInfo.componentStack}</pre>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;

